import React, {
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { ModalDelete } from "@/components/ModalDelete";
import { OverlayOwnerContext } from "@/components/overlay";
import { useModalFocus } from "@/hooks/useModalFocus";
import { useFormProtection } from "@/hooks/useFormProtection";

const modalStack: symbol[] = [];

export type ModalEffect = "zoom" | "fade" | "slide" | "none";

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    maxWidth?:
        | "xs"
        | "sm"
        | "md"
        | "lg"
        | "xl"
        | "2xl"
        | "3xl"
        | "4xl"
        | "5xl"
        | "6xl"
        | "7xl"
        | "full"
        | (string & {});
    children: React.ReactNode;
    footer?: React.ReactNode;
    onSubmit?: () => void | Promise<void>;
    protectChanges?: boolean;
    hasUnsavedChanges?: boolean;
    submitLabel?: string;
    submitVariant?: "primary" | "secondary" | "danger" | "warning";
    cancelLabel?: string;
    isSubmitting?: boolean;
    submittingLabel?: string;
    submitDisabled?: boolean;
    submitIcon?: React.ReactNode | string;
    hideCancel?: boolean;
    hideFooter?: boolean;
    hideClose?: boolean;
    hideFullscreen?: boolean;
    headerActions?: React.ReactNode;
    headerLeftActions?: React.ReactNode;
    isFullscreen?: boolean;
    onFullscreenChange?: (fullscreen: boolean) => void;
    headerClassName?: string;
    bodyClassName?: string;
    contentClassName?: string;
    equalFooterButtons?: boolean;
    footerButtonClassName?: string;
    effect?: ModalEffect;
    zIndex?: number;
    initialRect?: DOMRect | null;
    getOriginRect?: () => DOMRect | null;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    maxWidth = "md",
    children,
    footer,
    onSubmit,
    protectChanges = Boolean(onSubmit),
    hasUnsavedChanges,
    submitLabel,
    submitVariant = "primary",
    cancelLabel,
    isSubmitting = false,
    submittingLabel,
    submitDisabled = false,
    submitIcon,
    hideCancel = false,
    hideFooter = false,
    hideClose = false,
    hideFullscreen = false,
    headerActions,
    isFullscreen: controlledFullscreen,
    onFullscreenChange,
    headerClassName = "",
    bodyClassName = "",
    contentClassName = "",
    equalFooterButtons = true,
    footerButtonClassName = "",
    effect = "zoom",
    zIndex,
    headerLeftActions,
    initialRect,
    getOriginRect,
}) => {
    const { t } = useTranslation("global");
    const modalRef = useRef<HTMLDivElement>(null);
    const modalId = useId();
    const [internalFullscreen, setInternalFullscreen] =
        useState<boolean>(false);
    const isFullscreen = controlledFullscreen ?? internalFullscreen;

    // Fade effect: mounted = DOM present, visible = opacity/transform applied
    const ANIM_DURATION = 280;
    const [mounted, setMounted] = useState<boolean>(isOpen);
    const protection = useFormProtection(
        modalId,
        isOpen && mounted,
        protectChanges,
        modalRef,
        hasUnsavedChanges
    );
    const close = (): void => {
        if (!isSubmitting) {
            protection.close(onClose);
        }
    };
    const submitLock = useRef(false);
    const submit = async (): Promise<void> => {
        if (submitLock.current || isSubmitting || submitDisabled) {
            return;
        }
        submitLock.current = true;
        try {
            await onSubmit?.();
        } finally {
            submitLock.current = false;
            requestAnimationFrame(() => {
                const invalid = modalRef.current?.querySelector<HTMLElement>(
                    '[aria-invalid="true"]'
                );
                invalid?.focus();
                invalid?.scrollIntoView({ block: "nearest" });
            });
        }
    };

    const [visible, setVisible] = useState<boolean>(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previousBounds = useRef<{
        rect: DOMRect;
        fullscreen: boolean;
    } | null>(null);

    useEffect(() => {
        let firstFrame = 0;
        let secondFrame = 0;
        if (isOpen) {
            setMounted(true);
            firstFrame = requestAnimationFrame(() => {
                secondFrame = requestAnimationFrame(() => setVisible(true));
            });
        } else {
            setVisible(false);
            const targetRect = getOriginRect?.() || initialRect;
            const element = modalRef.current;
            if (
                element &&
                targetRect &&
                isFullscreen &&
                effect !== "none" &&
                !window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ) {
                const rect = element.getBoundingClientRect();
                element.animate(
                    [
                        {
                            transformOrigin: "top left",
                            transform: "none",
                            opacity: 1,
                        },
                        {
                            transformOrigin: "top left",
                            transform: `translate(${targetRect.x - rect.x}px, ${targetRect.y - rect.y}px) scale(${targetRect.width / rect.width}, ${targetRect.height / rect.height})`,
                            opacity: 0,
                        },
                    ],
                    {
                        duration: ANIM_DURATION,
                        easing: "cubic-bezier(.2,.8,.2,1)",
                    }
                );
            }
            closeTimerRef.current = setTimeout(
                () => {
                    setMounted(false);
                    setInternalFullscreen(false);
                },
                effect === "none" ? 0 : ANIM_DURATION
            );
        }
        return () => {
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
        };
    }, [isOpen, effect, isFullscreen, initialRect, getOriginRect]);

    const stackId = useRef(Symbol("modal"));
    const [, refreshStack] = useState(0);
    useModalFocus(isOpen && mounted, modalId, modalRef);
    useEffect(() => {
        if (!mounted) return;
        modalStack.push(stackId.current);
        refreshStack((value) => value + 1);
        return () => {
            const index = modalStack.indexOf(stackId.current);
            if (index >= 0) {
                modalStack.splice(index, 1);
            }
            refreshStack((value) => value + 1);
        };
    }, [mounted]);

    const stackIndex = modalStack.indexOf(stackId.current);
    const resolvedZIndex =
        zIndex ?? 100000 + Math.max(0, stackIndex) * 10;

    useLayoutEffect(() => {
        const element = modalRef.current;
        if (!element || !mounted) {
            previousBounds.current = null;
            return;
        }

        const rect = element.getBoundingClientRect();
        let previous = previousBounds.current;
        const isOriginExpansion =
            !previous && isFullscreen && Boolean(initialRect || getOriginRect);
        if (!previous && isFullscreen) {
            const initial = initialRect || getOriginRect?.() || null;
            if (initial) {
                previous = { rect: initial, fullscreen: false };
            }
        }

        if (
            previous &&
            previous.fullscreen !== isFullscreen &&
            !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            const fromRect = previous.rect;
            const toRect = rect;

            if (isOriginExpansion) {
                element.animate(
                    [
                        {
                            transformOrigin: "top left",
                            transform: `translate(${fromRect.x - toRect.x}px, ${fromRect.y - toRect.y}px) scale(${fromRect.width / toRect.width}, ${fromRect.height / toRect.height})`,
                        },
                        { transformOrigin: "top left", transform: "none" },
                    ],
                    {
                        duration: ANIM_DURATION,
                        easing: "cubic-bezier(.16,1,.3,1)",
                    }
                );
            } else {
                const isExpanding = isFullscreen;
                const deltaX =
                    fromRect.left +
                    fromRect.width / 2 -
                    (toRect.left + toRect.width / 2);
                const deltaY =
                    fromRect.top +
                    fromRect.height / 2 -
                    (toRect.top + toRect.height / 2);

                element.animate(
                    [
                        {
                            width: `${fromRect.width}px`,
                            height: `${fromRect.height}px`,
                            maxWidth: `${fromRect.width}px`,
                            maxHeight: `${fromRect.height}px`,
                            borderRadius: isExpanding ? "0.5rem" : "0px",
                            transform:
                                Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5
                                    ? `translate(${deltaX}px, ${deltaY}px)`
                                    : "none",
                        },
                        {
                            width: `${toRect.width}px`,
                            height: `${toRect.height}px`,
                            maxWidth: isExpanding
                                ? "none"
                                : `${toRect.width}px`,
                            maxHeight: isExpanding
                                ? "none"
                                : `${toRect.height}px`,
                            borderRadius: isExpanding ? "0px" : "0.5rem",
                            transform: "none",
                        },
                    ],
                    {
                        duration: 300,
                        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
                    }
                );
            }
        }

        previousBounds.current = { rect, fullscreen: isFullscreen };
    }, [isFullscreen, mounted, initialRect, getOriginRect]);

    const toggleFullscreen = (): void => {
        const next = !isFullscreen;
        setInternalFullscreen(next);
        onFullscreenChange?.(next);
    };

    const resolvedSubmitLabel = submitLabel ?? t("modal.save");
    const resolvedCancelLabel = cancelLabel ?? t("modal.cancel");
    const resolvedSubmittingLabel = submittingLabel ?? t("modal.saving");

    const buttonSizeClass = equalFooterButtons
        ? `flex-1 min-w-[120px] max-w-[180px] justify-center ${footerButtonClassName}`.trim()
        : footerButtonClassName;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                !e.defaultPrevented &&
                (e.key === "Escape" || e.key === "Esc") &&
                isOpen &&
                !isSubmitting &&
                modalStack.at(-1) === stackId.current
            ) {
                e.stopImmediatePropagation();
                if (isFullscreen) {
                    toggleFullscreen();
                    return;
                }
                close();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, isSubmitting, isFullscreen, onClose]);

    const isMouseDownOnBackdrop = useRef<boolean>(false);

    const handleBackdropMouseDown = (
        e: React.MouseEvent<HTMLDivElement>
    ): void => {
        isMouseDownOnBackdrop.current = e.target === e.currentTarget;
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (
            isMouseDownOnBackdrop.current &&
            e.target === e.currentTarget &&
            !isSubmitting
        ) {
            close();
        }
        isMouseDownOnBackdrop.current = false;
    };

    if (!mounted) {
        return null;
    }

    const maxWidthClasses: Record<string, string> = {
        xs: "max-w-xs",
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
        "5xl": "max-w-5xl",
        "6xl": "max-w-6xl",
        "7xl": "max-w-7xl",
        full: "max-w-full",
    };

    const widthClass = maxWidth.startsWith("max-w-")
        ? maxWidth
        : maxWidthClasses[maxWidth] || maxWidth;
    const hasHeader = Boolean(title || subtitle);

    const backdropTransitionClass =
        effect === "none"
            ? ""
            : `modal-backdrop-fade motion-reduce:transition-none ${visible ? "opacity-100" : "opacity-0"}`;

    let contentTransitionClass = "";
    if (effect !== "none") {
        if (isFullscreen && (initialRect || getOriginRect)) {
            contentTransitionClass = `modal-backdrop-fade motion-reduce:transition-none ${visible ? "opacity-100" : "opacity-0"}`;
        } else {
            switch (effect) {
                case "slide":
                    contentTransitionClass = `modal-content-slide motion-reduce:transition-none ${
                        visible
                            ? "opacity-100 translate-y-0 scale-100"
                            : "opacity-0 translate-y-6 scale-95"
                    }`;
                    break;

                case "fade":
                    contentTransitionClass = `modal-content-fade motion-reduce:transition-none ${
                        visible ? "opacity-100" : "opacity-0"
                    }`;
                    break;

                case "zoom":
                default:
                    contentTransitionClass = `modal-content-zoom motion-reduce:transition-none ${
                        visible ? "opacity-100 scale-100" : "opacity-0 scale-90"
                    }`;
                    break;
            }
        }
    }

    const modalContent = (
        <div
            onMouseDown={handleBackdropMouseDown}
            onClick={handleBackdropClick}
            className={`fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-xs transition-[padding] duration-300 ease-out ${backdropTransitionClass} ${
                isFullscreen ? "p-0" : "p-4"
            }`}
            style={{ zIndex: resolvedZIndex }}
        >
            <div
                ref={modalRef}
                role="dialog"
                tabIndex={-1}
                inert={!isOpen}
                aria-modal="true"
                aria-labelledby={title ? `${modalId}-title` : undefined}
                aria-label={title ? undefined : t("workspace.form.dialog")}
                aria-describedby={
                    subtitle ? `${modalId}-description` : undefined
                }
                onPointerDownCapture={protection.capture}
                onKeyDownCapture={protection.capture}
                onChangeCapture={protection.capture}
                onInputCapture={protection.capture}
                onClick={(e) => e.stopPropagation()}
                className={`w-full shadow-2xl flex flex-col overflow-hidden ${contentTransitionClass} ${
                    isFullscreen
                        ? "w-screen h-screen max-w-none max-h-none rounded-none"
                        : `max-h-[90vh] ${widthClass} rounded-lg`
                } ${contentClassName}`}
            >
                {/* Modal Header: Centered title and subtitle */}
                {hasHeader && (
                    <div
                        className={`relative px-10 py-3 bg-slate-800 dark:bg-slate-900 text-white flex flex-col items-center justify-center text-center shrink-0 ${
                            isFullscreen ? "rounded-none" : "rounded-t-lg"
                        } ${headerClassName}`}
                    >
                        {!hideFullscreen && !headerLeftActions && (
                            <button
                                type="button"
                                onClick={toggleFullscreen}
                                disabled={isSubmitting}
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/80 hover:text-white px-1 pb-1 transition-all cursor-pointer disabled:opacity-50 hover:scale-[1.2]"
                                title={
                                    isFullscreen
                                        ? t("modal.fullscreen_exit")
                                        : t("modal.fullscreen_enter")
                                }
                            >
                                <Icon
                                    value={
                                        isFullscreen ? "minimize" : "maximize"
                                    }
                                    size={18}
                                />
                            </button>
                        )}
                        {headerLeftActions && (
                            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {headerLeftActions}
                            </div>
                        )}
                        {title && (
                            <h2
                                id={`${modalId}-title`}
                                className="text-base font-bold text-white tracking-tight max-w-full"
                            >
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <p
                                id={`${modalId}-description`}
                                className="text-xs text-white/80 max-w-full mt-0.5"
                            >
                                {subtitle}
                            </p>
                        )}
                        {protection.dirty && (
                            <span
                                role="status"
                                className="mt-1 text-xs text-amber-200"
                            >
                                {t("workspace.form.unsaved")}
                            </span>
                        )}
                        {!hideClose && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {headerActions}
                                <button
                                    type="button"
                                    onClick={close}
                                    disabled={isSubmitting}
                                    className="text-white/80 hover:text-white px-1 pb-1 transition-all cursor-pointer disabled:opacity-50 hover:scale-[1.2]"
                                    title={t("modal.close")}
                                >
                                    <Icon value="x" size={22} />
                                </button>
                            </div>
                        )}
                        {hideClose && headerActions && (
                            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {headerActions}
                            </div>
                        )}
                    </div>
                )}

                {/* Modal Body & Footer container: border applied here */}
                <div
                    className={`bg-white dark:bg-dark-box ${
                        isFullscreen
                            ? "rounded-none border-none"
                            : hasHeader
                              ? "rounded-b-lg border-x border-b border-slate-200 dark:border-white/25"
                              : "rounded-lg border border-slate-200 dark:border-white/25"
                    } flex-1 flex flex-col min-h-0`}
                >
                    {/* Modal Body */}
                    <div
                        className={`flex-1 min-h-0 ${bodyClassName || "p-6 overflow-y-auto space-y-4"}`}
                    >
                        {children}
                    </div>

                    {/* Modal Footer: Centered buttons */}
                    {!hideFooter && (
                        <div className="flex shrink-0 items-center justify-center space-x-2.5 border-t border-slate-200/70 px-6 py-1.5 dark:border-white/10">
                            {footer || (
                                <>
                                    {!hideCancel && (
                                        <button
                                            type="button"
                                            onClick={close}
                                            disabled={isSubmitting}
                                            className={`btn secondary ${buttonSizeClass}`}
                                        >
                                            {resolvedCancelLabel}
                                        </button>
                                    )}

                                    {onSubmit && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                void submit();
                                            }}
                                            disabled={
                                                submitDisabled || isSubmitting
                                            }
                                            className={`btn ${submitVariant === "danger" ? "danger" : submitVariant === "warning" ? "warning" : submitVariant === "secondary" ? "secondary" : "primary"} ${buttonSizeClass}`}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Icon
                                                        value="loader"
                                                        size={14}
                                                        className="animate-spin"
                                                    />
                                                    <span>
                                                        {
                                                            resolvedSubmittingLabel
                                                        }
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    {typeof submitIcon ===
                                                    "string" ? (
                                                        <Icon
                                                            value={submitIcon}
                                                        />
                                                    ) : (
                                                        submitIcon
                                                    )}
                                                    <span>
                                                        {resolvedSubmitLabel}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (typeof document !== "undefined") {
        return createPortal(
            <OverlayOwnerContext.Provider value={modalId}>
                {modalContent}
                {protection.pending && (
                    <ModalDelete
                        isOpen
                        onClose={protection.cancel}
                        onConfirm={protection.discard}
                        title={t("workspace.form.discard_title")}
                        description={t("workspace.form.discard_description")}
                        itemName={title || t("workspace.form.unsaved")}
                        submitLabel={t("workspace.form.discard")}
                    />
                )}
            </OverlayOwnerContext.Provider>,
            document.body
        );
    }

    return null;
};

export default Modal;
